import { requestMagicLink } from "@src/appV2/Auth/cognito/cognitoAuth";
import { mapCognitoError } from "@src/appV2/Auth/cognito/mapCognitoError";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  logAuthRedirectFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  setLoginIdentityProvider,
} from "@src/appV2/Auth/loginRum";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";

interface SendLoginEmailParams {
  email: string;
  requestedURL?: string | undefined;
  /**
   * Skip the `ensureCognitoUser` JIT-import step. Set this on a resend: the
   * user was already imported on the initial send, so re-running it just fires
   * the PreSignUp Lambda again for no benefit (DEVOP-5526). Leave it off for
   * the first send so legacy users are still imported.
   */
  skipEnsureUser?: boolean;
  /**
   * Cognito username resolved by a previous send, passed on a resend so it
   * reuses that username instead of the (possibly-inactive) email alias.
   */
  username?: string;
}

export type SendLoginEmailResult =
  | { success: true; username: string }
  | { success: false; error: string };

/**
 * Sends a Cognito magic link login email.
 *
 * Delegates to `requestMagicLink`, which resolves the concrete Cognito username
 * via `ensureCognitoUser` (JIT-importing legacy workplace users who exist in
 * `facilityusers` but not yet in the Cognito user pool) and initiates the
 * CUSTOM_AUTH flow with that username rather than the raw email — an
 * `email_verified=false` user's email alias is inactive and can't initiate auth.
 * CUSTOM_AUTH does not invoke the PreAuthentication Lambda, so the import has to
 * be triggered from the client.
 *
 * On a resend (`skipEnsureUser`) the import is skipped and the caller passes the
 * `username` the initial send resolved, so it neither re-triggers the PreSignUp
 * Lambda nor falls back to the inactive email alias.
 */
export async function sendLoginEmail({
  email,
  skipEnsureUser = false,
  username: providedUsername,
}: SendLoginEmailParams): Promise<SendLoginEmailResult> {
  setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_MAGIC_LINK);

  try {
    const { username } = await requestMagicLink({
      email,
      skipEnsureUser,
      username: providedUsername,
    });
    logEvent(APP_V2_APP_EVENTS.COGNITO_MAGIC_LINK_REQUESTED, { outcome: LOGIN_OUTCOME.SUCCESS });
    return { success: true, username };
  } catch (error: unknown) {
    const mappedError = mapCognitoError(error, "magic-link-request");
    const { outcome, reason } =
      mappedError.code === "auth/unknown" && isNetworkFailure(error)
        ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
        : classifyCognitoError(mappedError.code);
    logAuthRedirectFailure({
      error,
      metadata: {
        errorCode: mappedError.code,
        errorMessage: mappedError.message,
        email,
        outcome,
        reason,
      },
    });
    return { success: false, error: mappedError.message };
  }
}
